import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { msalConfig, loginRequest } from './authConfig';

import Logo from './iwc-logo.png';

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// ==================== API SERVICE ====================
const API_BASE = '/api';
const AUTH_STORAGE_KEY = 'bookingApp.auth';
const SETTINGS_STORAGE_KEY = 'bookingApp.settings';

// The current acting user, attached to every request as X-Actor-* headers so
// the backend can record who performed each audited action.
let auditActor = null;
function setAuditActor(user) {
    auditActor = user ? { id: user.id, name: user.name, email: user.email } : null;
}
function actorHeaders() {
    if (!auditActor) return {};
    const headers = {};
    // Names/emails may contain non-Latin-1 chars; URI-encode so fetch can send
    // them as header values. The backend decodes with decodeURIComponent.
    if (auditActor.id) headers['X-Actor-Id'] = encodeURIComponent(auditActor.id);
    if (auditActor.name) headers['X-Actor-Name'] = encodeURIComponent(auditActor.name);
    if (auditActor.email) headers['X-Actor-Email'] = encodeURIComponent(auditActor.email);
    return headers;
}

async function fetchAPI(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;
    const config = {
        headers: { 'Content-Type': 'application/json', ...actorHeaders(), ...options.headers },
        ...options,
    };
    try {
        const response = await fetch(url, config);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(error.error || `HTTP ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error(`API Error [${endpoint}]:`, error);
        throw error;
    }
}

const api = {
    bookings: {
        getAll: (filters = {}) => {
            const params = new URLSearchParams();
            if (filters.date) params.append('date', filters.date);
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            if (filters.resourceId) params.append('resourceId', filters.resourceId);
            if (filters.userId) params.append('userId', filters.userId);
            const query = params.toString();
            return fetchAPI(`/bookings${query ? `?${query}` : ''}`);
        },
        create: (booking) => fetchAPI('/bookings', { method: 'POST', body: JSON.stringify(booking) }),
        update: (id, booking) => fetchAPI(`/bookings/${id}`, { method: 'PUT', body: JSON.stringify(booking) }),
        delete: (id, options = {}) => {
            const params = new URLSearchParams();
            if (options.mode) params.append('mode', options.mode);
            if (options.date) params.append('date', options.date);
            const query = params.toString();
            return fetchAPI(`/bookings/${id}${query ? `?${query}` : ''}`, { method: 'DELETE' });
        },
    },
    resources: {
        getAll: () => fetchAPI('/resources'),
        create: (resource) => fetchAPI('/resources', { method: 'POST', body: JSON.stringify(resource) }),
        update: (id, resource) => fetchAPI(`/resources/${id}`, { method: 'PUT', body: JSON.stringify(resource) }),
        delete: (id) => fetchAPI(`/resources/${id}`, { method: 'DELETE' }),
    },
    users: {
        getAll: () => fetchAPI('/users'),
        create: (user) => fetchAPI('/users', { method: 'POST', body: JSON.stringify(user) }),
        update: (id, user) => fetchAPI(`/users/${id}`, { method: 'PUT', body: JSON.stringify(user) }),
        delete: (id) => fetchAPI(`/users/${id}`, { method: 'DELETE' }),
        sync: (userInfo) => fetchAPI('/users/sync', { method: 'POST', body: JSON.stringify(userInfo) }),
    },
    departments: {
        getAll: () => fetchAPI('/departments'),
        create: (dept) => fetchAPI('/departments', { method: 'POST', body: JSON.stringify(dept) }),
        update: (id, dept) => fetchAPI(`/departments/${id}`, { method: 'PUT', body: JSON.stringify(dept) }),
        delete: (id) => fetchAPI(`/departments/${id}`, { method: 'DELETE' }),
    },
    seed: () => fetchAPI('/seed', { method: 'POST' }),
    audit: {
        list: (filters = {}) => {
            const params = new URLSearchParams();
            if (filters.limit) params.append('limit', filters.limit);
            if (filters.category) params.append('category', filters.category);
            if (filters.actorId) params.append('actorId', filters.actorId);
            if (filters.from) params.append('from', filters.from);
            if (filters.to) params.append('to', filters.to);
            const query = params.toString();
            return fetchAPI(`/audit${query ? `?${query}` : ''}`);
        },
        // Record a client-originated event (e.g. logout, session timeout).
        log: (event) => fetchAPI('/audit', { method: 'POST', body: JSON.stringify(event) }),
    },
    bookingAudit: {
        list: (filters = {}) => {
            const params = new URLSearchParams();
            if (filters.limit) params.append('limit', filters.limit);
            if (filters.action) params.append('action', filters.action);
            if (filters.actorId) params.append('actorId', filters.actorId);
            if (filters.from) params.append('from', filters.from);
            if (filters.to) params.append('to', filters.to);
            const query = params.toString();
            return fetchAPI(`/booking-logs${query ? `?${query}` : ''}`);
        },
    },
    auth: {
        loginLocal: (email, password) =>
            fetchAPI('/auth/login', {
                method: 'POST',
                body: JSON.stringify({ email, password }),
            }),
        // New SSO login endpoint
        loginSSO: (idToken, account) =>
            fetchAPI('/auth/sso', {
                method: 'POST',
                body: JSON.stringify({ idToken, account }),
            }),
    },
};

// ==================== HELPER FUNCTIONS ====================
function formatDate(dateInput) {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);

    const parts = new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Brisbane',   // AEST, no DST
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);

    const lookup = Object.fromEntries(parts.map(p => [p.type, p.value]));
    // YYYY-MM-DD in AEST
    return `${lookup.year}-${lookup.month}-${lookup.day}`;
}

function formatDisplayDate(dateInput) {
    if (!dateInput) return '';  // nothing to format

    const date = dateInput instanceof Date
        ? dateInput
        : new Date(dateInput);

    if (Number.isNaN(date.getTime())) {
        console.warn('formatDisplayDate: invalid date input', dateInput);
        return '';  // or 'Invalid date'
    }

    return new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Brisbane',   // AEST, no daylight savings
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
}

function formatHeaderDateAest(dateInput = new Date()) {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (Number.isNaN(date.getTime())) return '';

    const parts = new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Brisbane',
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).formatToParts(date);

    const lookup = Object.fromEntries(parts.map(p => [p.type, p.value]));
    return `${lookup.weekday} ${lookup.day} ${lookup.month} ${lookup.year}`;
}

// Friendly labels and badge colours for audit log entries.
const AUDIT_ACTION_LABELS = {
    'login.success': 'Login',
    'login.failure': 'Login failed',
    'logout': 'Logout',
    'session.timeout': 'Session timeout',
    'user.created': 'User created',
    'user.updated': 'User updated',
    'user.password_changed': 'Password changed',
    'user.role_changed': 'Role changed',
    'user.deleted': 'User deleted',
    'resource.created': 'Resource created',
    'resource.updated': 'Resource updated',
    'resource.deleted': 'Resource deleted',
    'department.created': 'Department created',
    'department.updated': 'Department updated',
    'department.deleted': 'Department deleted',
    'booking.created': 'Booking created',
    'booking.updated': 'Booking updated',
    'booking.deleted': 'Booking deleted',
};

function auditActionLabel(action) {
    return AUDIT_ACTION_LABELS[action] || action;
}

function auditCategoryBadge(category) {
    switch (category) {
        case 'auth': return 'bg-blue-100 text-blue-700';
        case 'user': return 'bg-purple-100 text-purple-700';
        case 'resource': return 'bg-emerald-100 text-emerald-700';
        case 'department': return 'bg-amber-100 text-amber-700';
        case 'booking': return 'bg-indigo-100 text-indigo-700';
        default: return 'bg-slate-100 text-slate-600';
    }
}

function generateTimeSlots(interval = 30) {
    const slots = [];
    for (let hour = 7; hour < 19; hour++) {
        for (let min = 0; min < 60; min += interval) {
            const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
            slots.push(time);
        }
    }
    return slots;
}

function timeToMinutes(time) {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
}

function minutesToTime(mins) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

const LOCAL_IDLE_MS = 60 * 60 * 1000; // 1 hour

function readStoredAuth() {
    try {
        const parsed = JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY));
        if (!parsed?.user) return null;
        if (parsed.method === 'local') {
            const lastActive = parsed.lastActiveAt || parsed.loggedInAt;
            if (!lastActive || Date.now() - new Date(lastActive).getTime() > LOCAL_IDLE_MS) {
                localStorage.removeItem(AUTH_STORAGE_KEY);
                return null;
            }
        }
        return parsed;
    } catch {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        return null;
    }
}

// ==================== ICONS ====================
const Icons = {
    Home: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>,
    Calendar: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
    Bookings: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>,
    Resources: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
    Car: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8m-8 5h8m-4-9v2m-4 4h.01M8 15h.01M16 15h.01M5 11l1.5-4.5A2 2 0 018.4 5h7.2a2 2 0 011.9 1.5L19 11m-14 0h14v6a2 2 0 01-2 2H7a2 2 0 01-2-2v-6z" /></svg>,
    Users: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>,
    Departments: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
    Settings: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    Search: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
    Plus: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>,
    Edit: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
    Delete: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
    Close: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>,
    ChevronLeft: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>,
    ChevronRight: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>,
    Repeat: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>,
    Microsoft: () => <svg className="w-5 h-5" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" /><rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" /></svg>,
    Meeting: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    SignOut: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>,
    Loader: () => <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>,
    Database: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" /></svg>,
    ChevronUp: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>,
    ChevronDown: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>,
    GripVertical: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h.01M8 12h.01M8 18h.01M16 6h.01M16 12h.01M16 18h.01" /></svg>,
    Reports: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
};

// ==================== RECURRING DELETE MODAL ====================
const RecurringDeleteModal = ({ isOpen, onClose, onConfirm, bookingTitle, occurrenceDate }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                        Delete Recurring Booking
                    </h3>
                    <p className="text-slate-600 mb-6">
                        "{bookingTitle}" is a recurring booking. What would you like to delete?
                    </p>
                    
                    <div className="space-y-3">
                        <button
                            onClick={() => onConfirm('single')}
                            className="w-full px-4 py-3 text-left rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                        >
                            <div className="font-medium text-slate-900">This occurrence only</div>
                            <div className="text-sm text-slate-500">
                                Delete only the booking on {occurrenceDate}
                            </div>
                        </button>
                        
                        <button
                            onClick={() => onConfirm('thisAndFuture')}
                            className="w-full px-4 py-3 text-left rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                        >
                            <div className="font-medium text-slate-900">This and future occurrences</div>
                            <div className="text-sm text-slate-500">
                                Delete this and all future bookings in the series
                            </div>
                        </button>
                        
                        <button
                            onClick={() => onConfirm('all')}
                            className="w-full px-4 py-3 text-left rounded-xl border border-red-200 hover:bg-red-50 text-red-700 transition-colors"
                        >
                            <div className="font-medium">Delete entire series</div>
                            <div className="text-sm text-red-500">
                                Delete all occurrences of this recurring booking
                            </div>
                        </button>
                    </div>
                </div>
                
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-white transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==================== RECURRING EDIT MODAL ====================
const RecurringEditModal = ({ isOpen, onClose, onConfirm, bookingTitle, occurrenceDate }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                        Edit Recurring Booking
                    </h3>
                    <p className="text-slate-600 mb-6">
                        "{bookingTitle}" is a recurring booking. What would you like to edit?
                    </p>
                    
                    <div className="space-y-3">
                        <button
                            onClick={() => onConfirm('single')}
                            className="w-full px-4 py-3 text-left rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                        >
                            <div className="font-medium text-slate-900">This occurrence only</div>
                            <div className="text-sm text-slate-500">
                                Edit only the booking on {occurrenceDate}
                            </div>
                        </button>
                        
                        <button
                            onClick={() => onConfirm('all')}
                            className="w-full px-4 py-3 text-left rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                        >
                            <div className="font-medium text-slate-900">All occurrences</div>
                            <div className="text-sm text-slate-500">
                                Edit the entire recurring series
                            </div>
                        </button>
                    </div>
                </div>
                
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-white transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==================== MODAL COMPONENT ====================
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
    if (!isOpen) return null;
    const sizeClasses = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>
                <div className={`relative inline-block w-full ${sizeClasses[size]} p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-2xl rounded-2xl`}>
                    <div className="flex items-center justify-between mb-5">
                        <h3 className="text-xl font-semibold text-slate-800">{title}</h3>
                        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
                            <Icons.Close />
                        </button>
                    </div>
                    {children}
                </div>
            </div>
        </div>
    );
};

// ==================== LOADING SPINNER ====================
const LoadingSpinner = ({ message = "Loading..." }) => (
    <div className="flex flex-col items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
        <p className="text-slate-600">{message}</p>
    </div>
);

// ==================== LOGIN SCREEN ====================
const LoginScreen = ({ onMicrosoftLogin, onLocalLogin, isLoading, ssoError }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [localError, setLocalError] = useState('');
    // Local login is hidden by default — most users sign in with Microsoft 365.
    const [showLocalLogin, setShowLocalLogin] = useState(false);

    const handleLocalSubmit = async (e) => {
        e.preventDefault();
        setLocalError('');
        try {
            await onLocalLogin({ email, password });
        } catch (err) {
            setLocalError(err.message || 'Login failed');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl"></div>
            </div>
            <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl p-10 w-full max-w-md border border-white/20 shadow-2xl">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-160 h-24">
                        <img src={Logo} alt="IWC Logo" style={{ width: '160px', marginBottom: '24px' }} />
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">Resource Booking</h1>
                    <p className="text-blue-200/80">Book meeting rooms and vehicles</p>
                </div>

                {/* Error display */}
                {(ssoError || localError) && (
                    <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-200 text-sm">
                        {ssoError || localError}
                    </div>
                )}

                {/* Microsoft 365 SSO - Primary option */}
                <button
                    onClick={onMicrosoftLogin}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-3 bg-white hover:bg-slate-50 text-slate-800 font-medium py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 mb-6"
                >
                    {isLoading ? (
                        <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                    ) : (
                        <>
                            <Icons.Microsoft />
                            <span>Sign in with Microsoft 365</span>
                        </>
                    )}
                </button>

                {!showLocalLogin ? (
                    <>
                        <p className="text-center text-blue-200/60 text-sm mb-6">
                            Use your organisation Microsoft 365 account for easy sign-in
                        </p>
                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => setShowLocalLogin(true)}
                                className="text-sm text-blue-200/70 hover:text-white underline underline-offset-4 transition-colors"
                            >
                                Use a local account instead
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="relative mb-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-white/10" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-transparent px-2 text-blue-100/70">or use local account</span>
                            </div>
                        </div>

                        {/* Local login form - Secondary option */}
                        <form onSubmit={handleLocalSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-blue-100 mb-1">Email</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white placeholder-blue-200/60 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                                    placeholder="you@example.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-blue-100 mb-1">Password</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white placeholder-blue-200/60 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                                    placeholder="••••••••"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full flex items-center justify-center gap-2 rounded-xl bg-slate-900/80 text-white py-2.5 text-sm font-medium hover:bg-slate-900 disabled:opacity-50"
                            >
                                {isLoading ? 'Signing in...' : 'Sign in with local account'}
                            </button>
                        </form>

                        <div className="text-center mt-4">
                            <button
                                type="button"
                                onClick={() => setShowLocalLogin(false)}
                                className="text-sm text-blue-200/70 hover:text-white underline underline-offset-4 transition-colors"
                            >
                                ← Back to Microsoft 365 sign-in
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// ==================== CALENDAR VIEW ====================
// Defined at module level (not inside BookingSystemInner) so background data
// refreshes re-render it in place instead of remounting it, which would reset
// the scroll position and selected state.
const CalendarView = ({
    bookings,
    selectedDate,
    setSelectedDate,
    selectedCategory,
    setSelectedCategory,
    settings,
    timeSlots,
    filteredResources,
    users,
    departments,
    currentUser,
    openModal,
    canUserEdit,
}) => {
    const dayBookings = bookings.filter(b => b.date === selectedDate);

    // 64px per hour, scaled to the configured slot interval
    const slotHeight = (64 * settings.timeInterval) / 60;
    const dayEndMinutes = settings.endHour * 60;

    const getBookingStyle = (booking) => {
        const startMin = timeToMinutes(booking.startTime);
        const endMin = timeToMinutes(booking.endTime);
        const dayStart = settings.startHour * 60;
        const totalMinutes = (settings.endHour - settings.startHour) * 60;
        const top = ((startMin - dayStart) / totalMinutes) * 100;
        const height = ((endMin - startMin) / totalMinutes) * 100;
        return { top: `${top}%`, height: `${height}%` };
    };

    const getBookingColor = (booking) => {
        const user = users.find(u => u.id === booking.bookedFor);
        const dept = departments.find(d => d.id === user?.departmentId);
        return dept?.color || '#3B82F6';
    };

    const changeDate = (days) => {
        const date = new Date(selectedDate);
        date.setDate(date.getDate() + days);
        setSelectedDate(formatDate(date));
    };

    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-6">
         <div className="flex items-center gap-4">
          <button onClick={() => changeDate(-1)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors"><Icons.ChevronLeft /></button>
                    <h2 className="text-xl font-semibold text-slate-800">{formatHeaderDateAest(selectedDate)}</h2>
            <button onClick={() => changeDate(1)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors"><Icons.ChevronRight /></button>
            <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
          <button onClick={() => setSelectedDate(formatDate(new Date()))} className="px-3 py-2 text-sm text-blue-600 border border-blue-100 rounded-lg hover:bg-blue-50 transition-colors">Today</button>
          </div>
          <div className="flex items-center gap-3">
            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="px-4 py-2 rounded-xl border border-slate-200 focus:border-blue-500 outline-none text-sm">
              <option value="all">All Resources</option>
              <option value="meeting_room">Meeting Rooms</option>
              <option value="car">Vehicles</option>
            </select>
            <button onClick={() => openModal('booking')} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all">
              <Icons.Plus /><span>New Booking</span>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex min-w-max">
            <div className="w-20 flex-shrink-0 border-r border-slate-200 bg-slate-50 sticky left-0 z-20">
              <div className="h-16 border-b border-slate-200 sticky top-0 z-30 bg-slate-50"></div>
              {timeSlots.filter(t => t.endsWith(':00')).map(time => (
                <div key={time} className="h-16 px-3 py-1 text-xs text-slate-500 border-b border-slate-100">{time}</div>
              ))}
            </div>
            {filteredResources.map(resource => {
              const resourceBookings = dayBookings.filter(b => b.resourceId === resource.id);

              // Handler for clicking on a time slot
              const handleSlotClick = (slotTime, e) => {
                // Don't trigger if clicking on a booking
                if (e.target.closest('.booking-item')) return;

                // Default duration is one slot, capped at the end of the day
                const endTime = minutesToTime(
                  Math.min(timeToMinutes(slotTime) + settings.timeInterval, dayEndMinutes)
                );

                // Open booking modal with pre-filled defaults
                openModal('booking', null, {
                  resourceId: resource.id,
                  date: selectedDate,
                  startTime: slotTime,
                  endTime: endTime,
                  bookedFor: currentUser?.id || ''
                });
              };

              return (
                <div key={resource.id} className="flex-1 min-w-[170px] border-r border-slate-200 last:border-r-0">
                  <div
                    className="h-16 px-3 py-2 border-b border-slate-200 bg-slate-50 sticky top-0 z-10"
                    title={`${resource.name}${resource.location ? ` — ${resource.location}` : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      {resource.type === 'car' ? <Icons.Car /> : <Icons.Meeting />}
                      <div className="min-w-0 flex-1">
                        <p
                          className="text-sm font-medium text-slate-800 leading-tight break-words"
                          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                        >
                          {resource.name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{resource.location}</p>
                      </div>
                    </div>
                  </div>
                  <div className="relative" style={{ height: `${timeSlots.length * slotHeight}px` }}>
                    {/* Clickable time slot rows */}
                    {timeSlots.map((slotTime, i) => {
                      const endsOnHour = minutesToTime(timeToMinutes(slotTime) + settings.timeInterval).endsWith(':00');
                      return (
                        <div
                          key={slotTime}
                          className={`absolute w-full border-b cursor-pointer hover:bg-blue-50/50 transition-colors group ${endsOnHour ? 'border-slate-200/80' : 'border-slate-100/80'}`}
                          style={{ top: `${i * slotHeight}px`, height: `${slotHeight}px` }}
                          onClick={(e) => handleSlotClick(slotTime, e)}
                          title={`Click to book ${resource.name} at ${slotTime}`}
                        >
                          {/* Subtle plus icon on hover */}
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-blue-500">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Bookings overlay */}
                    {resourceBookings.map(booking => {
                      const style = getBookingStyle(booking);
                      const color = getBookingColor(booking);
                      const user = users.find(u => u.id === booking.bookedFor);
                      return (
                        <div
                          key={booking.virtualId || booking.id}
                          className="booking-item absolute left-1 right-1 rounded-lg p-2 cursor-pointer overflow-hidden transition-all hover:shadow-lg z-10"
                          style={{ ...style, backgroundColor: color + '20', borderLeft: `3px solid ${color}` }}
                          onClick={(e) => {
                            e.stopPropagation();
                            canUserEdit(booking) && openModal('booking', booking);
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-slate-800 truncate">{booking.title}</p>
                              <p className="text-xs text-slate-600 truncate">{user?.name}</p>
                              <p className="text-xs text-slate-500">{booking.startTime} - {booking.endTime}</p>
                            </div>
                            {booking.isRecurring && <div className="text-slate-400 flex-shrink-0"><Icons.Repeat /></div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
};

// ==================== MAIN BOOKING SYSTEM (Inner Component) ====================
function BookingSystemInner() {
    const { instance, accounts } = useMsal();
    const isMsalAuthenticated = useIsAuthenticated();

    // Auth state — restored synchronously from localStorage for both local and
    // M365 logins, so the user never sees the login screen flash on refresh.
    // For M365, the MSAL effect below re-validates the session silently in the
    // background and signs the user out if the Microsoft session has expired.
    const [currentUser, setCurrentUser] = useState(() => readStoredAuth()?.user || null);
    const [isLoggedIn, setIsLoggedIn] = useState(() => !!readStoredAuth()?.user);
    const [authLoading, setAuthLoading] = useState(false);
    const [ssoError, setSsoError] = useState('');
    const [hasExplicitlyLoggedOut, setHasExplicitlyLoggedOut] = useState(false);

    // Tracks whether the MSAL session has been validated against the backend
    // this page load, so background re-validation runs exactly once.
    const ssoValidatedRef = useRef(false);

    // Handle MSAL authentication state changes
    useEffect(() => {
        const handleMsalAuth = async () => {
            // Don't auto-login if user has explicitly logged out
            if (hasExplicitlyLoggedOut) {
                return;
            }

            // Don't let a stale MSAL cache override an active local-account session
            if (isLoggedIn) {
                try {
                    const stored = JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY));
                    if (stored?.method === 'local') return;
                } catch { /* fall through to validation */ }
            }

            if (isMsalAuthenticated && accounts.length > 0 && !ssoValidatedRef.current) {
                ssoValidatedRef.current = true;
                // Only show the auth spinner if the user isn't already restored —
                // background re-validation should be invisible.
                const isBackgroundValidation = isLoggedIn;
                if (!isBackgroundValidation) setAuthLoading(true);
                setSsoError('');

                try {
                    // Get the ID token silently
                    const response = await instance.acquireTokenSilent({
                        ...loginRequest,
                        account: accounts[0],
                    });

                    // Send to backend for validation and user sync
                    const user = await api.auth.loginSSO(response.idToken, {
                        email: accounts[0].username,
                        name: accounts[0].name,
                        oid: accounts[0].localAccountId, // Azure AD Object ID
                    });

                    loginSuccess(user, 'm365');
                } catch (err) {
                    console.error('SSO login failed:', err);
                    ssoValidatedRef.current = false;
                    setSsoError(err.message || 'SSO login failed. Please try again.');
                    // Clear MSAL cache locally and return to the login screen
                    try {
                        instance.getAllAccounts().forEach(acc => instance.removeAccount(acc));
                        instance.setActiveAccount(null);
                    } catch (msalErr) {
                        console.error('MSAL cleanup error:', msalErr);
                    }
                    localStorage.removeItem(AUTH_STORAGE_KEY);
                    setCurrentUser(null);
                    setIsLoggedIn(false);
                } finally {
                    if (!isBackgroundValidation) setAuthLoading(false);
                }
            }
        };

        handleMsalAuth();
    }, [isMsalAuthenticated, accounts, instance, isLoggedIn, hasExplicitlyLoggedOut]);

    // Data state
    const [resources, setResources] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);

    // UI state
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentView, setCurrentView] = useState('home');
    const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [settings, setSettings] = useState(() => {
        const defaults = { timeInterval: 30, startHour: 7, endHour: 19 };
        try {
            const stored = JSON.parse(localStorage.getItem(SETTINGS_STORAGE_KEY));
            return stored ? { ...defaults, ...stored } : defaults;
        } catch {
            return defaults;
        }
    });

    useEffect(() => {
        localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    }, [settings]);
    const [bookingDefaults, setBookingDefaults] = useState(null);
    
    // State for recurring booking modals
    const [showRecurringDeleteModal, setShowRecurringDeleteModal] = useState(false);
    const [showRecurringEditModal, setShowRecurringEditModal] = useState(false);
    const [pendingRecurringAction, setPendingRecurringAction] = useState(null);
    const [editMode, setEditMode] = useState('all'); // 'single' or 'all'

    // ==================== DATA LOADING ====================
    // True once the first successful load has completed; later loads run
    // silently in the background instead of blanking the app with a spinner.
    const initialLoadDoneRef = useRef(false);

    const loadData = useCallback(async ({ silent = false } = {}) => {
        const isInitial = !initialLoadDoneRef.current;
        // Keep the previous state reference when the data hasn't changed, so
        // background polls don't cause any re-render or visual flicker.
        const keepIfSame = (setter, next) => {
            setter(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
        };
        try {
            if (isInitial && !silent) {
                setLoading(true);
                setError(null);
            }
            const [resourcesData, bookingsData, usersData, departmentsData] = await Promise.all([
                api.resources.getAll(),
                api.bookings.getAll(),
                api.users.getAll(),
                api.departments.getAll()
            ]);
            keepIfSame(setResources, resourcesData);
            keepIfSame(setBookings, bookingsData);
            keepIfSame(setUsers, usersData);
            keepIfSame(setDepartments, departmentsData);
            initialLoadDoneRef.current = true;
        } catch (err) {
            console.error('Error loading data:', err);
            // Only block the UI with an error on the very first load;
            // background refresh failures are retried on the next poll.
            if (isInitial && !silent) {
                setError('Failed to load data. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isLoggedIn) {
            loadData();
        }
    }, [isLoggedIn, loadData]);

    // Mirror modal state in a ref so the polling callback can check it
    // without re-subscribing the interval on every modal open/close.
    const modalOpenRef = useRef(false);
    useEffect(() => {
        modalOpenRef.current = isModalOpen || showRecurringDeleteModal || showRecurringEditModal;
    });

    // Auto-refresh: poll while the tab is visible and refresh on tab focus,
    // so other users' bookings appear without a manual reload.
    useEffect(() => {
        if (!isLoggedIn) return;

        const refresh = () => {
            if (document.visibilityState !== 'visible') return;
            if (modalOpenRef.current) return; // never wipe an open form
            loadData({ silent: true });
        };

        const intervalId = setInterval(refresh, 30000);
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        window.addEventListener('focus', handleVisibility);
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('focus', handleVisibility);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [isLoggedIn, loadData]);

    // ==================== AUTH HANDLERS ====================
    const loginSuccess = (user, method) => {
        setCurrentUser(user);
        setIsLoggedIn(true);
        setAuditActor(user); // Tag subsequent API calls with this actor

        // Persist to localStorage
        localStorage.setItem(
            AUTH_STORAGE_KEY,
            JSON.stringify({
                method, // 'local' or 'm365'
                user,
                loggedInAt: new Date().toISOString(),
            }),
        );
    };

    const handleMicrosoftLogin = async () => {
        setAuthLoading(true);
        setSsoError('');
        setHasExplicitlyLoggedOut(false); // Clear logout flag when user initiates login

        try {
            // This will open a popup for Microsoft login
            await instance.loginPopup(loginRequest);
            // The useEffect above will handle the rest after successful MSAL auth
        } catch (err) {
            console.error('Microsoft login failed:', err);
            if (err.errorCode === 'user_cancelled') {
                setSsoError('Login was cancelled.');
            } else {
                setSsoError(err.message || 'Microsoft login failed. Please try again.');
            }
            setAuthLoading(false);
        }
    };

    const handleLocalLogin = async ({ email, password }) => {
        if (!email || !password) {
            throw new Error('Email and password are required');
        }

        try {
            setAuthLoading(true);
            const user = await api.auth.loginLocal(email, password);
            loginSuccess(user, 'local');
        } catch (err) {
            console.error('Local login failed', err);
            throw err;
        } finally {
            setAuthLoading(false);
        }
    };

const handleLogout = useCallback(async (reason = 'manual') => {
    const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY);
    const authMethod = storedAuth ? JSON.parse(storedAuth).method : null;

    // Record the logout/session-timeout before clearing the actor. Best-effort:
    // fire-and-forget so a failed log never blocks sign-out. Headers are captured
    // synchronously by fetchAPI, so this must run before setAuditActor(null).
    api.audit.log({
        category: 'auth',
        action: reason === 'timeout' ? 'session.timeout' : 'logout',
        details: reason === 'timeout' ? 'Idle session timeout' : 'User signed out',
    }).catch(() => {});
    setAuditActor(null);

    // Clear local app state
    setCurrentUser(null);
    setIsLoggedIn(false);
    setHasExplicitlyLoggedOut(true); // Prevent auto-login from MSAL session
    localStorage.removeItem(AUTH_STORAGE_KEY);
    ssoValidatedRef.current = false;      // Next sign-in must validate again
    initialLoadDoneRef.current = false;   // Next sign-in does a fresh full load

    if (authMethod === 'm365') {
        // App-only logout: clear MSAL cache, but do NOT hit the Microsoft logout endpoint
        try {
            const allAccounts = instance.getAllAccounts();
            allAccounts.forEach(acc => instance.removeAccount(acc));
            instance.setActiveAccount(null);
        } catch (err) {
            console.error('MSAL local logout error:', err);
        }
    }
}, [instance, setCurrentUser, setIsLoggedIn, setHasExplicitlyLoggedOut]);



    // ==================== IDLE TIMEOUT (local login only) ====================
    useEffect(() => {
        if (!isLoggedIn) return;

        const stored = localStorage.getItem(AUTH_STORAGE_KEY);
        if (!stored) return;
        try {
            if (JSON.parse(stored).method !== 'local') return;
        } catch { return; }

        let timer = setTimeout(() => handleLogout('timeout'), LOCAL_IDLE_MS);

        const reset = () => {
            clearTimeout(timer);
            timer = setTimeout(() => handleLogout('timeout'), LOCAL_IDLE_MS);
            // Keep lastActiveAt fresh so a page reload also respects the idle window
            try {
                const stored = JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY));
                if (stored?.method === 'local') {
                    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ ...stored, lastActiveAt: new Date().toISOString() }));
                }
            } catch { /* ignore */ }
        };

        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
        events.forEach(e => window.addEventListener(e, reset, { passive: true }));

        return () => {
            clearTimeout(timer);
            events.forEach(e => window.removeEventListener(e, reset));
        };
    }, [isLoggedIn, handleLogout]);

    // Keep the audit actor in sync with the current user, including sessions
    // restored from localStorage on page refresh (where loginSuccess isn't called).
    useEffect(() => {
        setAuditActor(currentUser);
    }, [currentUser]);

    // ==================== PERMISSION HELPERS ====================
    const canUserEdit = (booking) => {
        if (!currentUser) return false;
        if (currentUser.role === 'admin') return true;
        if (currentUser.role === 'editor') return true;
        return booking.userId === currentUser.id || booking.bookedFor === currentUser.id;
    };

    const canManageResources = () => currentUser?.role === 'admin';
    const canManageUsers = () => currentUser?.role === 'admin';

    const canAccessResource = (resource, user = currentUser) => {
        if (!user || !resource) return false;
        if (user.role === 'admin' || user.role === 'editor') return true;
        const deptMatch = resource.departments?.includes(user.departmentId);
        const userMatch = resource.allowedUsers?.includes(user.id);
        return !!(deptMatch || userMatch);
    };

    const accessibleResources = useMemo(() => {
        if (!currentUser) return [];
        return resources.filter((r) => canAccessResource(r, currentUser));
    }, [resources, currentUser]);

    const accessibleResourceIds = useMemo(() => {
        return new Set(accessibleResources.map((r) => r.id));
    }, [accessibleResources]);

    // ==================== MODAL HANDLERS ====================
    const openModal = (type, item = null, defaults = null) => {
        // If editing a recurring booking, show the edit mode selection first
        if (type === 'booking' && item && item.isRecurring) {
            setPendingRecurringAction({ type, item, defaults });
            setShowRecurringEditModal(true);
            return;
        }
        
        setModalType(type);
        setEditingItem(item);
        setBookingDefaults(defaults);
        setEditMode('all');
        setIsModalOpen(true);
    };

    // Handler for when user selects edit mode for recurring booking
    const handleRecurringEditConfirm = (mode) => {
        setShowRecurringEditModal(false);
        setEditMode(mode);
        
        if (pendingRecurringAction) {
            setModalType(pendingRecurringAction.type);
            setEditingItem(pendingRecurringAction.item);
            setBookingDefaults(pendingRecurringAction.defaults);
            setIsModalOpen(true);
            setPendingRecurringAction(null);
        }
    };

    // Handler for recurring delete
    const handleRecurringDelete = async (booking) => {
        if (booking.isRecurring) {
            setPendingRecurringAction({ booking });
            setShowRecurringDeleteModal(true);
        } else {
            if (window.confirm('Are you sure you want to delete this booking?')) {
                try {
                    await api.bookings.delete(booking.id);
                    setBookings(prev => prev.filter(b => b.id !== booking.id));
                } catch (err) {
                    alert('Error deleting booking: ' + err.message);
                }
            }
        }
    };

    // Handler for when user confirms recurring delete mode
    const handleRecurringDeleteConfirm = async (mode) => {
        setShowRecurringDeleteModal(false);
        
        if (pendingRecurringAction?.booking) {
            const booking = pendingRecurringAction.booking;
            try {
                await api.bookings.delete(
                    booking.seriesId || booking.id,
                    { mode, date: booking.occurrenceDate || booking.date }
                );
                // Refresh bookings
                await loadData();
            } catch (err) {
                alert('Error deleting booking: ' + err.message);
            }
            setPendingRecurringAction(null);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalType(null);
        setEditingItem(null);
        setBookingDefaults(null);
    };

  // ==================== COMPUTED VALUES ====================
  const timeSlots = useMemo(() => generateTimeSlots(settings.timeInterval), [settings.timeInterval]);

const filteredResources = useMemo(() => {
  return resources
    .filter(r => {
      if (selectedCategory === 'all') return true;
      if (selectedCategory === 'meeting_room') return r.type === 'meeting_room';
      if (selectedCategory === 'car') return r.type === 'car';
      return true;
    })
    .filter(r => r.status === 'active')
    .filter(r => canAccessResource(r))
    .sort((a, b) => {
      // First sort by type: meeting_room before car
      if (a.type !== b.type) {
        return a.type === 'meeting_room' ? -1 : 1;
      }
      // Then sort by displayOrder (lower numbers first, undefined/null at the end)
      const orderA = a.displayOrder ?? 999999;
      const orderB = b.displayOrder ?? 999999;
      return orderA - orderB;
    });
}, [resources, selectedCategory, currentUser]);

const filteredBookings = useMemo(() => {
  return bookings
    // limit to bookings whose resources the current user can access
    .filter(b => {
      const resource = resources.find(r => r.id === b.resourceId);
      return resource ? canAccessResource(resource) : false;
    })
    // apply search
    .filter(b => {
      if (!searchQuery) return true;
      const resource = resources.find(r => r.id === b.resourceId);
      const user = users.find(u => u.id === b.bookedFor);
      const searchLower = searchQuery.toLowerCase();
      return (
        b.title?.toLowerCase().includes(searchLower) ||
        resource?.name?.toLowerCase().includes(searchLower) ||
        user?.name?.toLowerCase().includes(searchLower)
      );
    });
}, [bookings, searchQuery, resources, users, currentUser]);

  // ==================== BOOKING FORM ====================
const BookingForm = ({ onClose, booking = null, initialValues = {}, editMode = 'all' }) => {
  // Ensure we always have an object, even if null is passed in
  const safeInitialValues = initialValues || {};
  
  // Detect recurring booking
  const isRecurringOccurrence = booking?.isRecurring;
  const seriesId = booking?.seriesId || booking?.id;
  const occurrenceDate = booking?.occurrenceDate || booking?.date;

  const [formData, setFormData] = useState({
    resourceId:
      booking?.resourceId ||
      safeInitialValues.resourceId ||
      '',
    bookedFor:
      booking?.bookedFor ||
      safeInitialValues.bookedFor ||
      currentUser?.id ||
      '',
    title:
      booking?.title ||
      safeInitialValues.title ||
      '',
    description:
      booking?.description ||
      safeInitialValues.description ||
      '',
    date:
      booking?.date ||
      safeInitialValues.date ||
      selectedDate,
    startTime:
      booking?.startTime ||
      safeInitialValues.startTime ||
      '09:00',
    endTime:
      booking?.endTime ||
      safeInitialValues.endTime ||
      '10:00',
    attendees:
      booking?.attendees ||
      safeInitialValues.attendees ||
      1,
    recurring:
      booking?.recurrence?.pattern ||
      booking?.recurring ||
      safeInitialValues.recurring ||
      '',
    // add this so the recurring end date is controlled
    recurringEndDate:
      booking?.recurrence?.endDate ||
      booking?.recurringEndDate ||
      safeInitialValues.recurringEndDate ||
      ''
  });
    const [saving, setSaving] = useState(false);
      const [formError, setFormError] = useState(null);

      const handleDelete = async () => {
          if (!booking) return;
          
          // For recurring bookings, use the parent's handler
          if (isRecurringOccurrence) {
              handleRecurringDelete(booking);
              onClose();
              return;
          }
          
          if (!window.confirm('Are you sure you want to delete this booking?')) return;

          try {
              await api.bookings.delete(booking.id);
              setBookings(prev => prev.filter(b => b.id !== booking.id));
              onClose();
          } catch (err) {
              setFormError(err.message);
          }
      };

const handleSubmit = async (e) => {
  e.preventDefault();
  setSaving(true);
  setFormError(null);

  try {
    if (booking) {
      // EDITING an existing booking
      if (isRecurringOccurrence && editMode === 'single') {
        // Editing single occurrence of a recurring booking
        await api.bookings.update(seriesId, {
          ...formData,
          updateMode: 'single',
          occurrenceDate: occurrenceDate
        });
        await loadData(); // Refresh all bookings
      } else {
        // Editing entire series or non-recurring booking
        const updatePayload = {
          ...formData,
          userId: currentUser.id,
          updateMode: editMode
        };
        
        // Remove raw recurring fields and build proper recurrence object
        delete updatePayload.recurring;
        delete updatePayload.recurringEndDate;
        
        // Build recurrence object if recurring pattern is selected
        if (formData.recurring) {
          if (!formData.recurringEndDate) {
            throw new Error('Please select an end date for recurring bookings.');
          }
          if (formData.recurringEndDate < formData.date) {
            throw new Error('Recurring end date must be on or after the start date.');
          }
          updatePayload.recurrence = {
            pattern: formData.recurring,
            interval: booking?.recurrence?.interval || 1,
            endDate: formData.recurringEndDate
          };
          // Preserve daysOfWeek if it existed
          if (booking?.recurrence?.daysOfWeek) {
            updatePayload.recurrence.daysOfWeek = booking.recurrence.daysOfWeek;
          }
        }
        
        await api.bookings.update(booking.id, updatePayload);
        await loadData(); // Refresh to get updated occurrences
      }
    } else {
      // CREATING a new booking
      const basePayload = {
        ...formData,
        userId: currentUser.id,
        status: 'confirmed',
      };
      
      // Remove the 'recurring' and 'recurringEndDate' fields from payload 
      // as they're used to build the recurrence object
      delete basePayload.recurring;
      delete basePayload.recurringEndDate;
      
      // Validate required fields before sending
      if (!basePayload.resourceId) {
        throw new Error('Please select a resource');
      }
      if (!basePayload.bookedFor) {
        throw new Error('Please select who this booking is for');
      }

      // Add recurrence if selected (NEW: single entry with pattern)
      if (formData.recurring) {
        if (!formData.recurringEndDate) {
          throw new Error('Please select an end date for recurring bookings.');
        }

        if (formData.recurringEndDate < formData.date) {
          throw new Error('Recurring end date must be on or after the start date.');
        }

        basePayload.recurrence = {
          pattern: formData.recurring,
          interval: 1,
          endDate: formData.recurringEndDate
        };
      }

      // Create single booking (the backend handles recurrence expansion)
      await api.bookings.create(basePayload);
      await loadData(); // Refresh to get expanded occurrences
    }

    onClose();
  } catch (err) {
    setFormError(err.message);
  } finally {
    setSaving(false);
  }
};

      const meetingRooms = useMemo(
          () =>
              resources.filter(
                  (r) =>
                      r.type === 'meeting_room' &&
                      r.status === 'active' &&
                      (
                          currentUser?.role === 'admin' ||
                          canAccessResource(r, currentUser)
                      )
              ),
          [resources, currentUser]
      );

      const vehicles = useMemo(
          () =>
              resources.filter(
                  (r) =>
                      r.type === 'car' &&
                      r.status === 'active' &&
                      (
                          currentUser?.role === 'admin' ||
                          canAccessResource(r, currentUser)
                      )
              ),
          [resources, currentUser]
      );

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {formError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {formError}
          </div>
        )}
        {/* Recurring booking indicator */}
        {isRecurringOccurrence && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-700 text-sm flex items-center gap-2">
            <Icons.Repeat />
            <span>
              {editMode === 'single' 
                ? `Editing only ${formatDisplayDate(occurrenceDate)}`
                : 'Editing entire recurring series'}
            </span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Resource</label>
                    <select
                        value={formData.resourceId}
                        onChange={(e) =>
                            setFormData({ ...formData, resourceId: e.target.value })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                        required
                    >
                        <option value="">Select a resource</option>

                        <optgroup label="Meeting Rooms">
                            {meetingRooms.map((r) => (
                                <option key={r.id} value={r.id}>
                                    {r.name} (Capacity: {r.capacity})
                                </option>
                            ))}
                        </optgroup>

                        <optgroup label="Vehicles">
                            {vehicles.map((r) => (
                                <option key={r.id} value={r.id}>
                                    {r.name}
                                </option>
                            ))}
                        </optgroup>
                    </select>
          </div>
          
          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Book For</label>
            <select
              value={formData.bookedFor}
              onChange={(e) => setFormData({ ...formData, bookedFor: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              required
            >
              <option value="">Select a user</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              placeholder="Meeting title"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Attendees</label>
            <input
              type="number"
              value={formData.attendees}
              onChange={(e) => setFormData({ ...formData, attendees: Number(e.target.value) })}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              min="1"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Start Time</label>
            <select
              value={formData.startTime}
              onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              required
            >
              {timeSlots.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">End Time</label>
            <select
              value={formData.endTime}
              onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              required
            >
              {/* Include the day-end boundary so the last slot of the day can be booked */}
              {[...timeSlots, minutesToTime(settings.endHour * 60)].filter(t => t > formData.startTime).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Recurring</label>
            <select
              value={formData.recurring}
              onChange={(e) => setFormData({ ...formData, recurring: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
            >
              <option value="">No recurrence</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          {formData.recurring && (
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Recurring End Date
              </label>
              <input
                type="date"
                value={formData.recurringEndDate}
                min={formData.date}
                onChange={(e) => setFormData({ ...formData, recurringEndDate: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                required
              />
            </div>
          )}

          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none resize-none"
              rows="3"
              placeholder="Optional description"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                {booking && (
                    <button
                        type="button"
                        onClick={handleDelete}
                        disabled={saving}
                        className="px-4 py-2.5 rounded-xl border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 transition-colors"
                    >
                        Delete
                    </button>
                )}
                <button
                    type="button"
                    onClick={onClose}
                    disabled={saving}
                    className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-medium shadow-lg hover:shadow-blue-500/30 transition-all disabled:opacity-50"
                >
                    {saving ? 'Saving...' : (booking ? 'Update Booking' : 'Create Booking')}
                </button>
            </div>
      </form>
    );
  };

  // ==================== RESOURCE FORM ====================
  const ResourceForm = ({ onClose, resource = null }) => {
    const [formData, setFormData] = useState({
      name: resource?.name || '',
      type: resource?.type || 'meeting_room',
      capacity: resource?.capacity || 4,
      location: resource?.location || '',
      departments: resource?.departments || [],
      allowedUsers: resource?.allowedUsers || [],
      status: resource?.status || 'active',
    });
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);

    const handleUserAccessChange = (userId) => {
    setFormData(prev => ({
      ...prev,
    allowedUsers: prev.allowedUsers.includes(userId)
      ? prev.allowedUsers.filter(id => id !== userId)
      : [...prev.allowedUsers, userId],
  }));
};

const handleSubmit = async (e) => {
  e.preventDefault();
  setSaving(true);
  setFormError(null);

  try {
    if (resource) {
      const updated = await api.resources.update(resource.id, formData);
      setResources((prev) =>
        prev.map((r) => (r.id === resource.id ? updated : r))
      );
    } else {
      const newResource = await api.resources.create(formData);
      setResources((prev) => [...prev, newResource]);
    }

    onClose();
  } catch (err) {
    setFormError(err.message);
  } finally {
    setSaving(false);
  }
};

    const handleDeptChange = (deptId) => {
      setFormData(prev => ({
        ...prev,
        departments: prev.departments.includes(deptId)
          ? prev.departments.filter(d => d !== deptId)
          : [...prev.departments, deptId]
      }));
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {formError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{formError}</div>
        )}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Resource Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
            placeholder="e.g., Board Room A or Toyota Camry - ABC123"
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
            >
              <option value="meeting_room">Meeting Room</option>
              <option value="car">Vehicle</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Capacity</label>
            <input
              type="number"
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: Number(e.target.value) })}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              min="1"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
            placeholder="e.g., Level 1 or Car Park A"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Departments with Access</label>
          <div className="space-y-2">
            {departments.map(dept => (
              <label key={dept.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.departments.includes(dept.id)}
                  onChange={() => handleDeptChange(dept.id)}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">{dept.name}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
  <label className="block text-sm font-medium text-slate-700 mb-2">
    Specific Users with Access (optional)
  </label>
  <p className="text-xs text-slate-500 mb-2">
    These users can access this resource even if their department is not selected above.
  </p>
  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-xl p-3 bg-slate-50">
    <div className="space-y-1">
      {users.map(u => (
        <label key={u.id} className="flex items-center gap-2 cursor-pointer text-sm py-1 px-2 rounded-lg hover:bg-white transition-colors">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
            checked={formData.allowedUsers.includes(u.id)}
            onChange={() => handleUserAccessChange(u.id)}
          />
          <span className="text-slate-700 truncate">
            {u.name} <span className="text-slate-400 text-xs">({u.email})</span>
          </span>
        </label>
      ))}
    </div>
  </div>
  {formData.allowedUsers.length > 0 && (
    <p className="text-xs text-blue-600 mt-1">{formData.allowedUsers.length} user(s) selected</p>
  )}
</div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="maintenance">Under Maintenance</option>
          </select>
        </div>
        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium hover:shadow-lg hover:shadow-blue-500/30 transition-all disabled:opacity-50">
            {saving ? 'Saving...' : (resource ? 'Update Resource' : 'Add Resource')}
          </button>
        </div>
      </form>
    );
  };

  // ==================== USER FORM ====================
    const UserForm = ({ onClose, user = null }) => {
        const [formData, setFormData] = useState({
            name: user?.name || '',
            email: user?.email || '',
            departmentId: user?.departmentId || (departments[0]?.id || ''),
            role: user?.role || 'user',
        });
        const [password, setPassword] = useState('');
        const [saving, setSaving] = useState(false);
        const [formError, setFormError] = useState(null);

        // Preview what resources this user will have access to
        const userResources = useMemo(() => {
            if (!user) return [];
            const effectiveUser = { ...user, ...formData };
            return resources.filter(
                (r) => r.status === 'active' && canAccessResource(r, effectiveUser)
            );
        }, [resources, formData, user]);

        const handleSubmit = async (e) => {
            e.preventDefault();
            setSaving(true);
            setFormError(null);

            try {
                const payload = {
                    name: formData.name,
                    email: formData.email,
                    departmentId: formData.departmentId,
                    role: formData.role,
                };

                if (password) {
                    payload.password = password;
                }

                if (user) {
                    const updated = await api.users.update(user.id, payload);
                    setUsers((prev) => prev.map((u) => (u.id === user.id ? updated : u)));

                    if (currentUser?.id === user.id) {
                        setCurrentUser(updated);
                    }
                } else {
                    const created = await api.users.create(payload);
                    setUsers((prev) => [...prev, created]);
                }

                onClose();
            } catch (err) {
                console.error('Error saving user', err);
                setFormError(err.message || 'Failed to save user');
            } finally {
                setSaving(false);
            }
        };

        return (
            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                {formError && (
                    <div className="p-2 text-xs rounded-lg bg-red-50 text-red-600 border border-red-100">
                        {formError}
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                        Name
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) =>
                            setFormData((prev) => ({ ...prev, name: e.target.value }))
                        }
                        autoComplete="off"  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                        Email
                    </label>
                    <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                            setFormData((prev) => ({ ...prev, email: e.target.value }))
                        }
                        autoComplete="off" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                        required
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Department
                        </label>
                        <select
                            value={formData.departmentId}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    departmentId: e.target.value,
                                }))
                            }
                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                        >
                            {departments.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Role
                        </label>
                        <select
                            value={formData.role}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, role: e.target.value }))
                            }
                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                        >
                            <option value="user">User</option>
                            <option value="editor">Editor</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                </div>

                {/* Password set / reset */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                        Set / Reset Password
                    </label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                        placeholder="Leave blank to keep existing password"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                        At least 8 characters. Leave empty to keep the current password.
                    </p>
                </div>

                {/* Resource access preview */}
                {user && (
                    <div className="mt-2 border-t border-slate-100 pt-3">
                        <h3 className="text-sm font-semibold text-slate-700 mb-2">
                            Resource access preview
                        </h3>
                        {userResources.length === 0 ? (
                            <p className="text-xs text-slate-500">
                                This user currently has no resources available based on their
                                department and explicit access.
                            </p>
                        ) : (
                            <ul className="space-y-1 max-h-40 overflow-auto text-xs text-slate-700">
                                {userResources.map((r) => (
                                    <li key={r.id} className="flex items-center gap-2">
                                        {r.type === 'car' ? <Icons.Car /> : <Icons.Meeting />}
                                        <span>{r.name}</span>
                                        <span className="text-slate-400">({r.location})</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-medium shadow-lg hover:shadow-blue-500/30 transition-all disabled:opacity-50"
                    >
                        {saving ? 'Saving�' : user ? 'Update User' : 'Create User'}
                    </button>
                </div>
            </form>
        );
    };
  // ==================== DEPARTMENT FORM ====================
  const DepartmentForm = ({ onClose, department = null }) => {
    const [formData, setFormData] = useState({
      name: department?.name || '',
      color: department?.color || '#3B82F6',
    });
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSaving(true);
      setFormError(null);
      try {
        if (department) {
          const updated = await api.departments.update(department.id, formData);
          setDepartments(prev => prev.map(d => d.id === department.id ? updated : d));
        } else {
          const newDept = await api.departments.create(formData);
          setDepartments(prev => [...prev, newDept]);
        }
        onClose();
      } catch (err) {
        setFormError(err.message);
      } finally {
        setSaving(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {formError && (<div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{formError}</div>)}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Department Name</label>
          <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Color</label>
          <input type="color" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} className="w-full h-12 rounded-xl border border-slate-200 cursor-pointer" />
        </div>
        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium hover:shadow-lg hover:shadow-blue-500/30 transition-all disabled:opacity-50">{saving ? 'Saving...' : (department ? 'Update' : 'Add Department')}</button>
        </div>
      </form>
    );
  };

  // ==================== BOOKINGS LIST VIEW ====================
    const BookingsListView = () => {
    const [bookingsStartDate, setBookingsStartDate] = useState(formatDate(new Date()));
    const [bookingsEndDate, setBookingsEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [localSearch, setLocalSearch] = useState(searchQuery); // Add local state
    const [selectedResourceFilter, setSelectedResourceFilter] = useState('');
    const [selectedUserFilter, setSelectedUserFilter] = useState('');

    // Sync local search to parent (debounced effect)
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(localSearch);
        }, 300);
        return () => clearTimeout(timer);
    }, [localSearch]);

    const deleteBookingHandler = (booking) => {
      handleRecurringDelete(booking);
    };

    // Filter bookings by date range and access
    const visibleBookings = useMemo(() => {
      let filtered = currentUser?.role === 'admin'
        ? filteredBookings
        : filteredBookings.filter((b) => accessibleResourceIds.has(b.resourceId));

      // Apply resource filter
      if (selectedResourceFilter) {
        filtered = filtered.filter(b => b.resourceId === selectedResourceFilter);
      }

      // Apply booked-for filter
      if (selectedUserFilter) {
        filtered = filtered.filter(b => b.bookedFor === selectedUserFilter);
      }

      // Apply date range filter
      if (bookingsStartDate) {
        filtered = filtered.filter(b => b.date >= bookingsStartDate);
      }
      if (bookingsEndDate) {
        filtered = filtered.filter(b => b.date <= bookingsEndDate);
      }

      // Sort by date and time
      filtered = [...filtered].sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.startTime.localeCompare(b.startTime);
      });

      return filtered;
    }, [filteredBookings, accessibleResourceIds, currentUser, bookingsStartDate, bookingsEndDate, selectedResourceFilter, selectedUserFilter]);

    // Pagination calculations
    const totalPages = Math.ceil(visibleBookings.length / pageSize);
    const paginatedBookings = useMemo(() => {
      const start = (currentPage - 1) * pageSize;
      return visibleBookings.slice(start, start + pageSize);
    }, [visibleBookings, currentPage, pageSize]);

    // Reset to page 1 when filters change
    useEffect(() => {
      setCurrentPage(1);
    }, [bookingsStartDate, bookingsEndDate, pageSize, searchQuery, selectedResourceFilter, selectedUserFilter]);

    const clearDateFilters = () => {
      setBookingsStartDate(formatDate(new Date()));
      setBookingsEndDate('');
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-slate-800">All Bookings</h2>
          <button onClick={() => openModal('booking')} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all">
            <Icons.Plus /><span>New Booking</span>
          </button>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">Search</label>
              <div className="relative">
                <input 
                type="text" 
                placeholder="Search bookings..." 
                value={localSearch} 
                onChange={(e) => setLocalSearch(e.target.value)} 
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none" 
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Icons.Search /></div>
              </div>
            </div>
            
            {/* Resource Filter */}
            <div className="min-w-[180px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">Resource</label>
              <select
                value={selectedResourceFilter}
                onChange={(e) => setSelectedResourceFilter(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none bg-white"
              >
                <option value="">All Resources</option>
                {accessibleResources.filter(r => r.status === 'active').map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>

            {/* Booked For Filter */}
            <div className="min-w-[180px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">Booked For</label>
              <select
                value={selectedUserFilter}
                onChange={(e) => setSelectedUserFilter(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none bg-white"
              >
                <option value="">All Users</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div className="min-w-[150px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">From Date</label>
              <input 
                type="date" 
                value={bookingsStartDate} 
                onChange={(e) => setBookingsStartDate(e.target.value)} 
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              />
            </div>
            <div className="min-w-[150px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">To Date</label>
              <input 
                type="date" 
                value={bookingsEndDate} 
                onChange={(e) => setBookingsEndDate(e.target.value)}
                min={bookingsStartDate}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              />
            </div>
            
            {/* Clear Filters */}
            <button 
              onClick={clearDateFilters}
              className="px-4 py-2.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
            >
              Reset Dates
            </button>
          </div>
          
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>Showing {visibleBookings.length} booking{visibleBookings.length !== 1 ? 's' : ''}</span>
            {bookingsEndDate && (
              <span>Date range: {new Date(bookingsStartDate).toLocaleDateString('en-AU')} - {new Date(bookingsEndDate).toLocaleDateString('en-AU')}</span>
            )}
          </div>
        </div>

        {/* Bookings Table */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Resource</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Title</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Booked For</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Date</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Time</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
                 {paginatedBookings.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-12 text-center text-slate-500">No bookings found</td></tr>
              ) : (
                  paginatedBookings.map(booking => {
                  const resource = resources.find(r => r.id === booking.resourceId);
                  const user = users.find(u => u.id === booking.bookedFor);
                  return (
                    <tr key={booking.virtualId || booking.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {resource?.type === 'car' ? <Icons.Car /> : <Icons.Meeting />}
                          <span className="text-sm text-slate-800">{resource?.name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-800">{booking.title}</span>
                          {booking.isRecurring && (
                            <span className="text-blue-500" title="Recurring booking">
                              <Icons.Repeat />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{user?.name || 'Unknown'}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{new Date(booking.date).toLocaleDateString('en-AU')}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{booking.startTime} - {booking.endTime}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {canUserEdit(booking) && (
                            <>
                              <button onClick={() => openModal('booking', booking)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><Icons.Edit /></button>
                              <button onClick={() => deleteBookingHandler(booking)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Icons.Delete /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          
          {/* Pagination */}
          {visibleBookings.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-600">Rows per page:</span>
                <select 
                  value={pageSize} 
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">
                  {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, visibleBookings.length)} of {visibleBookings.length}
                </span>
                <div className="flex items-center gap-1 ml-4">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="First page"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Previous page"
                  >
                    <Icons.ChevronLeft />
                  </button>
                  <span className="px-3 py-1 text-sm font-medium text-slate-700">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Next page"
                  >
                    <Icons.ChevronRight />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage >= totalPages}
                    className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Last page"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

// ==================== RESOURCES VIEW ====================
const ResourcesView = () => {
  const deleteResource = async (id) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      try {
        await api.resources.delete(id);
        setResources(prev => prev.filter(r => r.id !== id));
      } catch (err) {
        alert('Error deleting resource: ' + err.message);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-800">Resources</h2>
        {canManageResources() && (
          <button
            onClick={() => openModal('resource')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all"
          >
            <Icons.Plus />
            <span>Add Resource</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {resources.length === 0 ? (
          <div className="col-span-full text-center py-12 text-slate-500">
            No resources found. Add your first resource!
          </div>
        ) : (
          resources.map((resource) => {
            const assignedDepts = departments.filter((d) =>
              resource.departments?.includes(d.id)
            );

            // NEW: resolve specific users with access
            const specificUsers = users.filter((u) =>
              resource.allowedUsers?.includes(u.id)
            );

            return (
              <div
                key={resource.id}
                className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-3 rounded-xl ${
                        resource.type === 'car'
                          ? 'bg-emerald-100 text-emerald-600'
                          : 'bg-blue-100 text-blue-600'
                      }`}
                    >
                      {resource.type === 'car' ? <Icons.Car /> : <Icons.Meeting />}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">
                        {resource.name}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {resource.location}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs rounded-lg ${
                      resource.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : resource.status === 'maintenance'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {resource.status}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Capacity:</span>{' '}
                    {resource.capacity}
                  </p>

                  {/* Departments with access */}
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-1">
                      Departments with access
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {assignedDepts.length === 0 ? (
                        <span className="text-xs text-slate-400">
                          No departments assigned
                        </span>
                      ) : (
                        assignedDepts.map((d) => (
                          <span
                            key={d.id}
                            className="px-2 py-1 text-xs rounded-lg"
                            style={{
                              backgroundColor: d.color + '20',
                              color: d.color,
                            }}
                          >
                            {d.name}
                          </span>
                        ))
                      )}
                    </div>
                  </div>

                  {/* NEW: Specific users with access */}
                  <div className="mt-2">
                    <p className="text-xs font-medium text-slate-500 mb-1">
                      Specific users with access
                    </p>
                    {specificUsers.length === 0 ? (
                      <p className="text-xs text-slate-400">
                        No individual user overrides
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {specificUsers.map((u) => (
                          <span
                            key={u.id}
                            className="px-2 py-1 text-xs rounded-lg bg-slate-100 text-slate-700"
                          >
                            {u.name}
                            <span className="text-[10px] text-slate-400 ml-1">
                              ({u.email})
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {canManageResources() && (
                  <div className="flex gap-2 pt-3 border-t border-slate-100">
                    <button
                      onClick={() => openModal('resource', resource)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Icons.Edit /> Edit
                    </button>
                    <button
                      onClick={() => deleteResource(resource.id)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Icons.Delete /> Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

  // ==================== USERS VIEW ====================
const UsersView = () => {
  // wherever users comes from originally – do NOT re-declare a new [users, setUsers] if you already get them via props/context
    const deleteUser = async (user) => {
        if (user.id === currentUser?.id) {
            alert("You cannot delete your own account!");
            return;
        }

        if (!window.confirm("Are you sure you want to delete this user?")) {
            return;
        }

        try {
            // use the shared API helper, which calls DELETE /api/users/:id
            await api.users.delete(user.id);

            // update local state so the row disappears without a full reload
            setUsers((prev) => prev.filter((u) => u.id !== user.id));
        } catch (err) {
            console.error("Error deleting user:", err);
            alert("Error deleting user: " + (err.message || "Unknown error"));
        }
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-slate-800">Users</h2>
          {canManageUsers() && (
            <button onClick={() => openModal('user')} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all">
              <Icons.Plus /><span>Add User</span>
            </button>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Name</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Email</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Department</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Role</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Last Login</th>
                {canManageUsers() && <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Actions</th>}
              </tr>
            </thead>
<tbody className="divide-y divide-slate-100">
  {users.length === 0 ? (
    <tr>
      <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
        No users found
      </td>
    </tr>
  ) : (
    users.map((user) => {
      const dept = departments.find(
        (d) =>
          d.id === user.departmentId || d.id === user.departmentid
      );

      const deptKey = user.departmentid ?? user.departmentId;
      
      // Format last login date
      const formatLastLogin = (lastLogin) => {
        if (!lastLogin) return 'Never';
        const date = new Date(lastLogin);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} min ago`;
        if (diffHours < 24) return `${diffHours} hr ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
      };

      return (
        <tr
          key={`${deptKey}-${user.id}`}
          className="hover:bg-slate-50 transition-colors"
        >
          <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium">{user.name?.charAt(0) || '?'}</div>
                          <span className="text-sm font-medium text-slate-800">{user.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{user.email}</td>
                      <td className="px-6 py-4">
                        {dept && <span className="px-2 py-1 text-xs rounded-lg" style={{ backgroundColor: dept.color + '20', color: dept.color }}>{dept.name}</span>}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-lg ${user.role === 'admin' ? 'bg-purple-100 text-purple-700' : user.role === 'editor' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{user.role}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">{formatLastLogin(user.lastLogin)}</td>
                      {canManageUsers() && (
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => openModal('user', user)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><Icons.Edit /></button>
                            <button onClick={() => deleteUser(user)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Icons.Delete /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ==================== DEPARTMENTS VIEW ====================
  const DepartmentsView = () => {
    const deleteDepartment = async (id) => {
      const usersInDept = users.filter(u => u.departmentId === id);
      if (usersInDept.length > 0) { alert('Cannot delete department with assigned users!'); return; }
      if (window.confirm('Are you sure you want to delete this department?')) {
        try {
          await api.departments.delete(id);
          setDepartments(prev => prev.filter(d => d.id !== id));
        } catch (err) {
          alert('Error deleting department: ' + err.message);
        }
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-slate-800">Departments</h2>
          {canManageUsers() && (
            <button onClick={() => openModal('department')} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all">
              <Icons.Plus /><span>Add Department</span>
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {departments.length === 0 ? (
            <div className="col-span-full text-center py-12 text-slate-500">No departments found. Add your first department!</div>
          ) : (
            departments.map(dept => {
              const deptUsers = users.filter(u => u.departmentId === dept.id);
              const deptResources = resources.filter(r => r.departments?.includes(dept.id));
              return (
                <div key={dept.id} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: dept.color }}></div>
                    <h3 className="font-semibold text-slate-800">{dept.name}</h3>
                  </div>
                  <div className="space-y-2 mb-4">
                    <p className="text-sm text-slate-600"><span className="font-medium">{deptUsers.length}</span> Users</p>
                    <p className="text-sm text-slate-600"><span className="font-medium">{deptResources.length}</span> Resources</p>
                  </div>
                  {canManageUsers() && (
                    <div className="flex gap-2 pt-3 border-t border-slate-100">
                      <button onClick={() => openModal('department', dept)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><Icons.Edit /> Edit</button>
                      <button onClick={() => deleteDepartment(dept.id)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Icons.Delete /> Delete</button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // ==================== SETTINGS VIEW ====================
  const SettingsView = () => {
    const [seeding, setSeeding] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);

    // Audit log (admin only)
    const isAdmin = currentUser?.role === 'admin';
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditError, setAuditError] = useState(null);
    const [auditCategory, setAuditCategory] = useState('');
    const [auditLimit, setAuditLimit] = useState(50);

    const loadAuditLogs = useCallback(async () => {
      if (!isAdmin) return;
      setAuditLoading(true);
      setAuditError(null);
      try {
        const logs = await api.audit.list({
          limit: auditLimit,
          category: auditCategory || undefined,
        });
        setAuditLogs(Array.isArray(logs) ? logs : []);
      } catch (err) {
        setAuditError(err.message || 'Failed to load audit log');
      } finally {
        setAuditLoading(false);
      }
    }, [isAdmin, auditLimit, auditCategory]);

    useEffect(() => {
      loadAuditLogs();
    }, [loadAuditLogs]);

    // Booking activity log (admin only, separate store)
    const [bookingLogs, setBookingLogs] = useState([]);
    const [bookingLogsLoading, setBookingLogsLoading] = useState(false);
    const [bookingLogsError, setBookingLogsError] = useState(null);
    const [bookingAction, setBookingAction] = useState('');
    const [bookingLimit, setBookingLimit] = useState(50);

    const loadBookingLogs = useCallback(async () => {
      if (!isAdmin) return;
      setBookingLogsLoading(true);
      setBookingLogsError(null);
      try {
        const logs = await api.bookingAudit.list({
          limit: bookingLimit,
          action: bookingAction || undefined,
        });
        setBookingLogs(Array.isArray(logs) ? logs : []);
      } catch (err) {
        setBookingLogsError(err.message || 'Failed to load booking activity');
      } finally {
        setBookingLogsLoading(false);
      }
    }, [isAdmin, bookingLimit, bookingAction]);

    useEffect(() => {
      loadBookingLogs();
    }, [loadBookingLogs]);

    const handleSeedDatabase = async () => {
      if (window.confirm('This will add sample data to the database. Continue?')) {
        setSeeding(true);
        try {
          await api.seed();
          await loadData();
          alert('Database seeded successfully!');
        } catch (err) {
          alert('Error seeding database: ' + err.message);
        } finally {
          setSeeding(false);
        }
      }
    };

    // Get resources sorted by type and current displayOrder
    const sortedMeetingRooms = resources
      .filter(r => r.type === 'meeting_room')
      .sort((a, b) => (a.displayOrder ?? 999999) - (b.displayOrder ?? 999999));
    
    const sortedVehicles = resources
      .filter(r => r.type === 'car')
      .sort((a, b) => (a.displayOrder ?? 999999) - (b.displayOrder ?? 999999));

    const moveResource = async (resourceId, direction, resourceList) => {
      const currentIndex = resourceList.findIndex(r => r.id === resourceId);
      if (currentIndex === -1) return;
      
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= resourceList.length) return;

      setSavingOrder(true);
      try {
        // Reassign displayOrder for all resources in the list after the move
        const newList = [...resourceList];
        const [moved] = newList.splice(currentIndex, 1);
        newList.splice(newIndex, 0, moved);
        
        // Update all resources with their new positions
        const updatePromises = newList.map((resource, index) => 
          api.resources.update(resource.id, { displayOrder: index })
        );
        await Promise.all(updatePromises);

        // Refresh the resources
        await loadData();
      } catch (err) {
        alert('Error updating resource order: ' + err.message);
      } finally {
        setSavingOrder(false);
      }
    };

    // Drag and drop handlers
    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverItem, setDragOverItem] = useState(null);
    const [dragType, setDragType] = useState(null); // 'meeting_room' or 'car'

    const handleDragStart = (e, resource, type) => {
      setDraggedItem(resource);
      setDragType(type);
      e.dataTransfer.effectAllowed = 'move';
      // Add a slight delay to allow the drag image to be captured
      setTimeout(() => {
        e.target.style.opacity = '0.5';
      }, 0);
    };

    const handleDragEnd = (e) => {
      e.target.style.opacity = '1';
      setDraggedItem(null);
      setDragOverItem(null);
      setDragType(null);
    };

    const handleDragOver = (e, resource, type) => {
      e.preventDefault();
      // Only allow dropping on same type
      if (dragType !== type) return;
      if (resource.id !== dragOverItem?.id) {
        setDragOverItem(resource);
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
    };

    const handleDrop = async (e, targetResource, resourceList, type) => {
      e.preventDefault();
      
      // Only allow dropping on same type
      if (dragType !== type || !draggedItem || draggedItem.id === targetResource.id) {
        setDraggedItem(null);
        setDragOverItem(null);
        setDragType(null);
        return;
      }

      setSavingOrder(true);
      try {
        const draggedIndex = resourceList.findIndex(r => r.id === draggedItem.id);
        const targetIndex = resourceList.findIndex(r => r.id === targetResource.id);
        
        // Create new ordered list
        const newList = [...resourceList];
        const [moved] = newList.splice(draggedIndex, 1);
        newList.splice(targetIndex, 0, moved);
        
        // Update all resources with their new positions
        const updatePromises = newList.map((resource, index) => 
          api.resources.update(resource.id, { displayOrder: index })
        );
        await Promise.all(updatePromises);

        // Refresh the resources
        await loadData();
      } catch (err) {
        alert('Error updating resource order: ' + err.message);
      } finally {
        setSavingOrder(false);
        setDraggedItem(null);
        setDragOverItem(null);
        setDragType(null);
      }
    };

    const ResourceOrderList = ({ title, resourceList, icon: Icon, type }) => (
      <div className="mb-6 last:mb-0">
        <h4 className="text-sm font-medium text-slate-600 mb-3 flex items-center gap-2">
          <Icon />
          {title}
        </h4>
        {resourceList.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No {title.toLowerCase()} found</p>
        ) : (
          <div className="space-y-2">
            {resourceList.map((resource, index) => (
              <div
                key={resource.id}
                draggable
                onDragStart={(e) => handleDragStart(e, resource, type)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, resource, type)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, resource, resourceList, type)}
                className={`flex items-center gap-3 p-3 bg-slate-50 rounded-xl border-2 transition-all ${
                  dragOverItem?.id === resource.id && dragType === type
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-slate-200'
                } ${draggedItem?.id === resource.id ? 'opacity-50' : ''}`}
              >
                <div className="text-slate-400 cursor-grab active:cursor-grabbing">
                  <Icons.GripVertical />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 truncate">{resource.name}</p>
                  <p className="text-xs text-slate-500">{resource.location}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => moveResource(resource.id, 'up', resourceList)}
                    disabled={index === 0 || savingOrder}
                    className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move up"
                  >
                    <Icons.ChevronUp />
                  </button>
                  <button
                    onClick={() => moveResource(resource.id, 'down', resourceList)}
                    disabled={index === resourceList.length - 1 || savingOrder}
                    className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move down"
                  >
                    <Icons.ChevronDown />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );

    return (
      <div className="space-y-6 max-w-5xl">
        <h2 className="text-2xl font-semibold text-slate-800">Settings</h2>
        
        {/* Calendar Resource Order */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-slate-800">Calendar Resource Order</h3>
            <p className="text-sm text-slate-500 mt-1">
              Arrange the order resources appear on the calendar. Meeting rooms and vehicles are grouped separately.
            </p>
          </div>
          
          <ResourceOrderList 
            title="Meeting Rooms" 
            resourceList={sortedMeetingRooms} 
            icon={Icons.Meeting}
            type="meeting_room"
          />
          
          <div className="border-t border-slate-200 my-4"></div>
          
          <ResourceOrderList 
            title="Vehicles" 
            resourceList={sortedVehicles} 
            icon={Icons.Car}
            type="car"
          />
          
          {savingOrder && (
            <div className="mt-4 flex items-center gap-2 text-sm text-blue-600">
              <Icons.Loader />
              <span>Saving order...</span>
            </div>
          )}
        </div>

        {/* Booking Settings */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6">
          <div>
            <h3 className="text-lg font-medium text-slate-800 mb-4">Booking Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Time Interval (minutes)</label>
                <select value={settings.timeInterval} onChange={(e) => setSettings({ ...settings, timeInterval: Number(e.target.value) })} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none">
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={60}>60 minutes</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Audit Log (admin only) */}
        {isAdmin && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-medium text-slate-800">Audit Log</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Security-related activity: logins, logouts, and changes to users, resources, and departments.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={auditCategory}
                  onChange={(e) => setAuditCategory(e.target.value)}
                  className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                >
                  <option value="">All categories</option>
                  <option value="auth">Authentication</option>
                  <option value="user">Users</option>
                  <option value="resource">Resources</option>
                  <option value="department">Departments</option>
                </select>
                <button
                  onClick={loadAuditLogs}
                  disabled={auditLoading}
                  className="px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
                >
                  Refresh
                </button>
              </div>
            </div>

            {auditError && (
              <div className="mb-3 text-sm text-red-600">{auditError}</div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                    <th className="py-2 pr-4 font-medium">Time</th>
                    <th className="py-2 pr-4 font-medium">Actor</th>
                    <th className="py-2 pr-4 font-medium">Action</th>
                    <th className="py-2 pr-4 font-medium">Target</th>
                    <th className="py-2 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="align-top">
                      <td className="py-2 pr-4 whitespace-nowrap text-slate-500">{formatDisplayDate(log.timestamp)}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        <span className="text-slate-800">{log.actorName || log.actorEmail || 'Unknown'}</span>
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${auditCategoryBadge(log.category)}`}>
                          {auditActionLabel(log.action)}
                        </span>
                        {log.status === 'failure' && (
                          <span className="ml-1 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">failed</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-slate-600">{log.targetName || '—'}</td>
                      <td className="py-2 text-slate-500">{log.details || '—'}</td>
                    </tr>
                  ))}
                  {!auditLoading && auditLogs.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-slate-400">No audit activity recorded yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between">
              {auditLoading ? (
                <span className="text-sm text-slate-400">Loading…</span>
              ) : (
                <span className="text-sm text-slate-400">{auditLogs.length} {auditLogs.length === 1 ? 'entry' : 'entries'}</span>
              )}
              {auditLogs.length >= auditLimit && (
                <button
                  onClick={() => setAuditLimit((n) => n + 50)}
                  className="px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Load more
                </button>
              )}
            </div>
          </div>
        )}

        {/* Booking Activity (admin only, separate from the security audit log) */}
        {isAdmin && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-medium text-slate-800">Booking Activity</h3>
                <p className="text-sm text-slate-500 mt-1">
                  A record of bookings created, edited, and deleted.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={bookingAction}
                  onChange={(e) => setBookingAction(e.target.value)}
                  className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                >
                  <option value="">All activity</option>
                  <option value="booking.created">Created</option>
                  <option value="booking.updated">Updated</option>
                  <option value="booking.deleted">Deleted</option>
                </select>
                <button
                  onClick={loadBookingLogs}
                  disabled={bookingLogsLoading}
                  className="px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
                >
                  Refresh
                </button>
              </div>
            </div>

            {bookingLogsError && (
              <div className="mb-3 text-sm text-red-600">{bookingLogsError}</div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                    <th className="py-2 pr-4 font-medium">Time</th>
                    <th className="py-2 pr-4 font-medium">Actor</th>
                    <th className="py-2 pr-4 font-medium">Action</th>
                    <th className="py-2 pr-4 font-medium">Booking</th>
                    <th className="py-2 pr-4 font-medium">Resource</th>
                    <th className="py-2 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {bookingLogs.map((log) => (
                    <tr key={log.id} className="align-top">
                      <td className="py-2 pr-4 whitespace-nowrap text-slate-500">{formatDisplayDate(log.timestamp)}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        <span className="text-slate-800">{log.actorName || log.actorEmail || 'Unknown'}</span>
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${auditCategoryBadge(log.category)}`}>
                          {auditActionLabel(log.action)}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-slate-600">{log.targetName || '—'}</td>
                      <td className="py-2 pr-4 text-slate-600">{log.resourceName || '—'}</td>
                      <td className="py-2 text-slate-500">{log.details || '—'}</td>
                    </tr>
                  ))}
                  {!bookingLogsLoading && bookingLogs.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-slate-400">No booking activity recorded yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between">
              {bookingLogsLoading ? (
                <span className="text-sm text-slate-400">Loading…</span>
              ) : (
                <span className="text-sm text-slate-400">{bookingLogs.length} {bookingLogs.length === 1 ? 'entry' : 'entries'}</span>
              )}
              {bookingLogs.length >= bookingLimit && (
                <button
                  onClick={() => setBookingLimit((n) => n + 50)}
                  className="px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Load more
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ==================== HOME VIEW ====================
  const HomeView = () => {
    // Only show bookings for resources the current user has access to
    const todayBookings = bookings.filter(b => {
      if (b.date !== formatDate(new Date())) return false;
      const resource = resources.find(r => r.id === b.resourceId);
      return resource ? canAccessResource(resource) : false;
    });
    const myBookings = bookings.filter(b => b.bookedFor === currentUser?.id);
    // Only count resources the user has access to
    const accessibleMeetingRooms = accessibleResources.filter(r => r.type === 'meeting_room');
    const accessibleVehicles = accessibleResources.filter(r => r.type === 'car');

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Welcome back, {currentUser?.name?.split(' ')[0] || 'User'}</h1>
                    <p className="text-slate-500">{formatHeaderDateAest(new Date())}</p>
          </div>
          <button onClick={() => openModal('booking')} className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all">
            <Icons.Plus /><span>New Booking</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between mb-4"><Icons.Calendar /><span className="text-3xl font-bold">{todayBookings.length}</span></div>
            <p className="text-blue-100">Today's Bookings</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between mb-4"><Icons.Bookings /><span className="text-3xl font-bold">{myBookings.length}</span></div>
            <p className="text-emerald-100">My Bookings</p>
          </div>
          <div className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between mb-4"><Icons.Meeting /><span className="text-3xl font-bold">{accessibleMeetingRooms.length}</span></div>
            <p className="text-violet-100">Meeting Rooms</p>
          </div>
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between mb-4"><Icons.Car /><span className="text-3xl font-bold">{accessibleVehicles.length}</span></div>
            <p className="text-amber-100">Vehicles</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Today's Schedule</h3>
            {todayBookings.length > 0 ? (
              <div className="space-y-3">
                {todayBookings.slice(0, 5).map(booking => {
                  const resource = resources.find(r => r.id === booking.resourceId);
                  const user = users.find(u => u.id === booking.bookedFor);
                  const dept = departments.find(d => d.id === user?.departmentId);
                  return (
                    <div key={booking.virtualId || booking.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                      <div className="w-1 h-12 rounded-full" style={{ backgroundColor: dept?.color || '#3B82F6' }}></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{booking.title}</p>
                        <p className="text-sm text-slate-500">{resource?.name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-700">{booking.startTime}</p>
                        <p className="text-xs text-slate-400">{booking.endTime}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No bookings for today</p>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => { setCurrentView('calendar'); setSelectedCategory('meeting_room'); }} className="flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-all">
                <Icons.Meeting /><span className="text-sm text-slate-700">Book Meeting Room</span>
              </button>
              <button onClick={() => { setCurrentView('calendar'); setSelectedCategory('car'); }} className="flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 transition-all">
                <Icons.Car /><span className="text-sm text-slate-700">Book Vehicle</span>
              </button>
              <button onClick={() => setCurrentView('bookings')} className="flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50 transition-all">
                <Icons.Bookings /><span className="text-sm text-slate-700">View All Bookings</span>
              </button>
              {currentUser?.role === 'admin' && (
                <button onClick={() => setCurrentView('resources')} className="flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-200 hover:border-amber-300 hover:bg-amber-50 transition-all">
                  <Icons.Resources /><span className="text-sm text-slate-700">Manage Resources</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== REPORTS VIEW ====================
  const ReportsView = () => {
    const todayStr = formatDate(new Date());
    const firstOfMonth = (() => { const d = new Date(); d.setDate(1); return formatDate(d); })();

    const [reportType, setReportType] = useState('utilization');
    const [fromDate, setFromDate] = useState(firstOfMonth);
    const [toDate, setToDate] = useState(todayStr);

    const bookingDuration = (b) => timeToMinutes(b.endTime) - timeToMinutes(b.startTime);

    const rangeBookings = useMemo(() => {
      return bookings.filter(b => {
        if (fromDate && b.date < fromDate) return false;
        if (toDate && b.date > toDate) return false;
        return true;
      });
    }, [bookings, fromDate, toDate]);

    const totalBookings = rangeBookings.length;
    const totalHours = (rangeBookings.reduce((acc, b) => acc + bookingDuration(b), 0) / 60).toFixed(1);

    const utilizationData = useMemo(() => {
      const map = {};
      rangeBookings.forEach(b => {
        if (!map[b.resourceId]) {
          const res = resources.find(r => r.id === b.resourceId);
          map[b.resourceId] = {
            resourceName: res?.name || 'Unknown',
            type: res?.type === 'car' ? 'Vehicle' : res?.type === 'meeting_room' ? 'Meeting Room' : res?.type || 'Unknown',
            count: 0, totalMins: 0,
          };
        }
        map[b.resourceId].count += 1;
        map[b.resourceId].totalMins += bookingDuration(b);
      });
      return Object.values(map).sort((a, b) => b.count - a.count);
    }, [rangeBookings]);

    const byUserData = useMemo(() => {
      const map = {};
      rangeBookings.forEach(b => {
        const uid = b.bookedFor;
        if (!map[uid]) {
          const u = users.find(u => u.id === uid);
          const dept = departments.find(d => d.id === (u?.departmentId || u?.departmentid));
          map[uid] = { userName: u?.name || 'Unknown', email: u?.email || '', department: dept?.name || '—', count: 0, totalMins: 0 };
        }
        map[uid].count += 1;
        map[uid].totalMins += bookingDuration(b);
      });
      return Object.values(map).sort((a, b) => b.count - a.count);
    }, [rangeBookings]);

    const byDeptData = useMemo(() => {
      const map = {};
      rangeBookings.forEach(b => {
        const u = users.find(u => u.id === b.bookedFor);
        const deptId = u?.departmentId || u?.departmentid || '__unknown__';
        const dept = departments.find(d => d.id === deptId);
        if (!map[deptId]) map[deptId] = { department: dept?.name || 'Unknown', count: 0, totalMins: 0, uniqueUsers: new Set() };
        map[deptId].count += 1;
        map[deptId].totalMins += bookingDuration(b);
        map[deptId].uniqueUsers.add(b.bookedFor);
      });
      return Object.values(map).map(r => ({ ...r, uniqueUsers: r.uniqueUsers.size })).sort((a, b) => b.count - a.count);
    }, [rangeBookings]);

    const dailyData = useMemo(() => {
      const map = {};
      rangeBookings.forEach(b => {
        if (!map[b.date]) map[b.date] = { date: b.date, count: 0, totalMins: 0, uniqueResources: new Set() };
        map[b.date].count += 1;
        map[b.date].totalMins += bookingDuration(b);
        map[b.date].uniqueResources.add(b.resourceId);
      });
      return Object.values(map).map(r => ({ ...r, uniqueResources: r.uniqueResources.size })).sort((a, b) => a.date.localeCompare(b.date));
    }, [rangeBookings]);

    const exportCSV = () => {
      let headers = [], rows = [];
      if (reportType === 'utilization') {
        headers = ['Resource Name', 'Type', 'Booking Count', 'Total Hours', 'Avg Duration (mins)'];
        rows = utilizationData.map(r => [r.resourceName, r.type, r.count, (r.totalMins / 60).toFixed(2), r.count > 0 ? Math.round(r.totalMins / r.count) : 0]);
      } else if (reportType === 'byUser') {
        headers = ['User Name', 'Email', 'Department', 'Booking Count', 'Total Hours'];
        rows = byUserData.map(r => [r.userName, r.email, r.department, r.count, (r.totalMins / 60).toFixed(2)]);
      } else if (reportType === 'byDept') {
        headers = ['Department', 'Booking Count', 'Total Hours', 'Unique Users'];
        rows = byDeptData.map(r => [r.department, r.count, (r.totalMins / 60).toFixed(2), r.uniqueUsers]);
      } else if (reportType === 'daily') {
        headers = ['Date', 'Booking Count', 'Total Hours', 'Unique Resources Used'];
        rows = dailyData.map(r => [r.date, r.count, (r.totalMins / 60).toFixed(2), r.uniqueResources]);
      }
      const esc = (v) => { const s = String(v ?? ''); return (s.includes(',') || s.includes('"') || s.includes('\n')) ? `"${s.replace(/"/g, '""')}"` : s; };
      const csv = [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\r\n');
      const labels = { utilization: 'Resource_Utilization', byUser: 'Bookings_by_User', byDept: 'Bookings_by_Department', daily: 'Daily_Summary' };
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', `${labels[reportType]}_${fromDate}_to_${toDate}.csv`);
      document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
    };

    const reportTabs = [
      { id: 'utilization', label: 'Resource Utilization' },
      { id: 'byUser',      label: 'Bookings by User' },
      { id: 'byDept',      label: 'Bookings by Department' },
      { id: 'daily',       label: 'Daily Summary' },
    ];

    const renderTable = () => {
      if (reportType === 'utilization') return (
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Resource Name</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Type</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Booking Count</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Total Hours</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Avg Duration (mins)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {utilizationData.length === 0
              ? <tr><td colSpan="5" className="px-6 py-12 text-center text-slate-500">No data for this date range</td></tr>
              : utilizationData.map((row, i) => (
                <tr key={i} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-slate-800">{row.resourceName}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{row.type}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 text-right">{row.count}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 text-right">{(row.totalMins / 60).toFixed(1)}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 text-right">{row.count > 0 ? Math.round(row.totalMins / row.count) : 0}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      );
      if (reportType === 'byUser') return (
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">User Name</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Email</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Department</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Booking Count</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Total Hours</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {byUserData.length === 0
              ? <tr><td colSpan="5" className="px-6 py-12 text-center text-slate-500">No data for this date range</td></tr>
              : byUserData.map((row, i) => (
                <tr key={i} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-slate-800">{row.userName}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{row.email}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{row.department}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 text-right">{row.count}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 text-right">{(row.totalMins / 60).toFixed(1)}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      );
      if (reportType === 'byDept') return (
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Department</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Booking Count</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Total Hours</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Unique Users</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {byDeptData.length === 0
              ? <tr><td colSpan="4" className="px-6 py-12 text-center text-slate-500">No data for this date range</td></tr>
              : byDeptData.map((row, i) => (
                <tr key={i} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-slate-800">{row.department}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 text-right">{row.count}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 text-right">{(row.totalMins / 60).toFixed(1)}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 text-right">{row.uniqueUsers}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      );
      if (reportType === 'daily') return (
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Date</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Booking Count</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Total Hours</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Unique Resources Used</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {dailyData.length === 0
              ? <tr><td colSpan="4" className="px-6 py-12 text-center text-slate-500">No data for this date range</td></tr>
              : dailyData.map((row, i) => (
                <tr key={i} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-slate-800">{row.date}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 text-right">{row.count}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 text-right">{(row.totalMins / 60).toFixed(1)}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 text-right">{row.uniqueResources}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      );
      return null;
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-800">Reports &amp; Analytics</h2>
            <p className="text-sm text-slate-500 mt-1">Analytics calculated from all loaded bookings.</p>
          </div>
          <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl hover:shadow-lg hover:shadow-emerald-500/30 transition-all text-sm font-medium">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            Export CSV
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[160px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">From</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none" />
            </div>
            <div className="min-w-[160px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">To</label>
              <input type="date" value={toDate} min={fromDate} onChange={(e) => setToDate(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none" />
            </div>
            <button onClick={() => { setFromDate(firstOfMonth); setToDate(todayStr); }} className="px-4 py-2.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors">
              This Month
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {reportTabs.map(tab => (
              <button key={tab.id} onClick={() => setReportType(tab.id)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${reportType === tab.id ? 'bg-blue-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between mb-2"><Icons.Bookings /><span className="text-3xl font-bold">{totalBookings}</span></div>
            <p className="text-blue-100 text-sm">Total Bookings in Range</p>
          </div>
          <div className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between mb-2"><Icons.Calendar /><span className="text-3xl font-bold">{totalHours}</span></div>
            <p className="text-violet-100 text-sm">Total Hours Booked</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          {renderTable()}
        </div>
      </div>
    );
  };

    // ==================== RENDER ====================
    if (!isLoggedIn) {
        return (
            <LoginScreen
                onMicrosoftLogin={handleMicrosoftLogin}
                onLocalLogin={handleLocalLogin}
                isLoading={authLoading}
                ssoError={ssoError}
            />
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <LoadingSpinner message="Loading booking system..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
                    <p className="text-red-600 mb-4">{error}</p>
                    <button onClick={loadData} className="px-4 py-2 bg-blue-600 text-white rounded-xl">Retry</button>
                </div>
            </div>
        );
    }

    const renderView = () => {
        switch (currentView) {
            case 'home': return <HomeView />;
            case 'calendar': return (
                <CalendarView
                    bookings={bookings}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    settings={settings}
                    timeSlots={timeSlots}
                    filteredResources={filteredResources}
                    users={users}
                    departments={departments}
                    currentUser={currentUser}
                    openModal={openModal}
                    canUserEdit={canUserEdit}
                />
            );
            case 'bookings': return <BookingsListView />;
            case 'resources': return <ResourcesView />;
            case 'users': return <UsersView />;
            case 'departments': return <DepartmentsView />;
            case 'settings': return <SettingsView />;
            case 'reports': return currentUser?.role === 'admin' ? <ReportsView /> : <HomeView />;
            default: return <HomeView />;
        }
    };

    const renderModal = () => {
        try {
            switch (modalType) {
                case 'booking':
                    return (
                        <BookingForm
                            onClose={closeModal}
                            booking={editingItem}
                            initialValues={bookingDefaults || {}}
                            editMode={editMode}
                        />
                    );
                case 'resource':
                    return (
                        <ResourceForm
                            onClose={closeModal}
                            resource={editingItem}
                        />
                    );
                case 'user':
                    return (
                        <UserForm
                            onClose={closeModal}
                            user={editingItem}
                        />
                    );
                case 'department':
                    return (
                        <DepartmentForm
                            onClose={closeModal}
                            department={editingItem}
                        />
                    );
                default:
                    return null;
            }
        } catch (err) {
            console.error('Error rendering modal content:', err);
            return (
                <div className="text-sm text-red-600">
                    Error rendering form: {String(err.message || err)}
                </div>
            );
        }
    };

    const getModalTitle = () => {
        const action = editingItem ? 'Edit' : 'New';
        switch (modalType) {
            case 'booking':
                return `${action} Booking`;
            case 'resource':
                return `${action} Resource`;
            case 'user':
                return `${action} User`;
            case 'department':
                return `${action} Department`;
            default:
                return '';
        }
    };

    const navItems = [
        { id: 'home', icon: Icons.Home, label: 'Home' },
        { id: 'calendar', icon: Icons.Calendar, label: 'Calendar' },
        { id: 'bookings', icon: Icons.Bookings, label: 'Bookings' },
        { id: 'resources', icon: Icons.Resources, label: 'Resources', adminOnly: true },
        { id: 'users', icon: Icons.Users, label: 'Users', adminOnly: true },
        { id: 'departments', icon: Icons.Departments, label: 'Departments', adminOnly: true },
        { id: 'reports', icon: Icons.Reports, label: 'Reports', adminOnly: true },
        { id: 'settings', icon: Icons.Settings, label: 'Settings', adminOnly: true },
    ];

    return (
        <div className="h-screen bg-slate-50 flex" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
            <aside className="w-64 bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col">
                <div className="p-5 border-b border-slate-700/50">
                    <div className="flex items-center gap-3">
                        <div className="inline-flex items-center justify-center w-160 h-24">
                            <img src={Logo} alt="IWC Logo" style={{ width: '160px', marginBottom: '24px' }} />
                        </div>
                        <div>
                            <h1 className="font-bold text-white">Resource Booking</h1>
                            <p className="text-xs text-slate-400">Manage bookings</p>
                        </div>
                    </div>
                </div>
                <nav className="flex-1 p-4 space-y-1">
                    {navItems.filter(item => !item.adminOnly || currentUser?.role === 'admin').map(item => (
                        <button key={item.id} onClick={() => setCurrentView(item.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${currentView === item.id ? 'bg-blue-500/20 text-blue-400' : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-200'}`}>
                            <item.icon /><span className="text-sm font-medium">{item.label}</span>
                        </button>
                    ))}
                </nav>
                <div className="p-4 border-t border-slate-700/50">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium">{currentUser?.name?.charAt(0) || '?'}</div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{currentUser?.name}</p>
                            <p className="text-xs text-slate-400 capitalize">{currentUser?.role}</p>
                        </div>
                    </div>
                    <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-700/50 hover:bg-slate-700 text-slate-300 rounded-xl transition-colors">
                        <Icons.SignOut /><span className="text-sm">Sign Out</span>
                    </button>
                </div>
            </aside>
            <main className="flex-1 p-6 overflow-auto">{renderView()}</main>
            <Modal isOpen={isModalOpen} onClose={closeModal} title={getModalTitle()} size="md">{renderModal()}</Modal>
            
            {/* Recurring booking modals */}
            <RecurringDeleteModal
                isOpen={showRecurringDeleteModal}
                onClose={() => {
                    setShowRecurringDeleteModal(false);
                    setPendingRecurringAction(null);
                }}
                onConfirm={handleRecurringDeleteConfirm}
                bookingTitle={pendingRecurringAction?.booking?.title}
                occurrenceDate={formatDisplayDate(pendingRecurringAction?.booking?.occurrenceDate || pendingRecurringAction?.booking?.date || '')}
            />

            <RecurringEditModal
                isOpen={showRecurringEditModal}
                onClose={() => {
                    setShowRecurringEditModal(false);
                    setPendingRecurringAction(null);
                }}
                onConfirm={handleRecurringEditConfirm}
                bookingTitle={pendingRecurringAction?.item?.title}
                occurrenceDate={formatDisplayDate(pendingRecurringAction?.item?.occurrenceDate || pendingRecurringAction?.item?.date || '')}
            />
        </div>
    );
}

// ==================== APP WRAPPER WITH MSAL PROVIDER ====================
export default function BookingSystem() {
    return (
        <MsalProvider instance={msalInstance}>
            <BookingSystemInner />
        </MsalProvider>
    );
}